import React from 'react';
import * as Yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { MenuItem } from '@mui/material';
import { Formik } from 'formik';
import { JunoImage } from '@juno/client-api/fakeModel';
import { JunoUser, MentorshipStatusEnum, RoleEnum } from '@juno/client-api/model';
import { optimizeImage } from '@juno/utils';
import { GenericFormikInput } from '../GenericInputComponents';
import JunoImageUpload from '../JunoImageUpload';
import { ASPECT_RATIOS } from '../JunoImageUpload/constants';
import { DrawerFooter } from '../RightExpandableDrawer';

const UserSchema = Yup.object().shape({
  first_name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  last_name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  role: Yup.number().oneOf([RoleEnum.NUMBER_0, RoleEnum.NUMBER_1]),
  avatar: Yup.string(),
  // TODO tags and access passes
});

interface EditUserFormProps {
  currentUser: JunoUser | null;
  handleClose: () => void;
  handleSave: (user: JunoUser) => void;
  isNewUser?: boolean;
  isViewerStaff: boolean;
  isMentorshipEnabled?: boolean;
}

const EditUserForm: React.FC<EditUserFormProps> = ({
  currentUser,
  handleClose,
  handleSave,
  isNewUser,
  isViewerStaff,
  isMentorshipEnabled,
}) => {
  return (
    <Box>
      <Formik
        initialValues={currentUser || ({} as JunoUser)}
        onSubmit={handleSave}
        validationSchema={UserSchema}
        enableReinitialize
      >
        {(props) => (
          <>
            <Grid container spacing={1} pb={1}>
              <Grid item xs={12} justifyContent={'center'} display={'flex'}>
                <Stack alignItems={'center'}>
                  <JunoImageUpload
                    style={{
                      height: 120,
                      width: 120,
                      borderRadius: 150,
                      overflow: 'hidden',
                      marginBottom: 2,
                    }}
                    src={optimizeImage(270, currentUser?.avatar)}
                    srcUrl={currentUser?.avatar || ''}
                    onFileUploaded={(selected: JunoImage | null) => {
                      if (selected?.url) {
                        props.setFieldValue('avatar', selected?.url || '');
                        props.setFieldTouched('avatar', true);
                      }
                    }}
                    aspectRatios={[ASPECT_RATIOS.ONE_ONE]}
                    imageType='Avatar'
                    disabled={props.values.role === RoleEnum.NUMBER_2 && !isViewerStaff}
                  />
                  {currentUser?.id && !currentUser?.active && (
                    <Typography variant='subtitle2' color={'error'}>
                      User has been Deactivated
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} pt={2}>
                <GenericFormikInput
                  name='first_name'
                  value={props.values.first_name || ''}
                  label='First Name'
                  InputProps={{ disableUnderline: true }}
                  variant='outlined'
                  size='small'
                  disabled={props.values.role === RoleEnum.NUMBER_2 && !isViewerStaff}
                />
              </Grid>
              <Grid item xs={12}>
                <GenericFormikInput
                  name='last_name'
                  value={props.values.last_name || ''}
                  label='Last Name'
                  size='small'
                  InputProps={{ disableUnderline: true }}
                  variant='outlined'
                  disabled={props.values.role === RoleEnum.NUMBER_2 && !isViewerStaff}
                />
              </Grid>
              <Grid item xs={12}>
                <GenericFormikInput
                  name='email'
                  value={props.values.email || ''}
                  label='Email'
                  size='small'
                  disabled={currentUser?.id !== '' && !isNewUser}
                  InputProps={{ disableUnderline: true }}
                  variant='outlined'
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id='role_select_label'>Role</InputLabel>
                  <Select
                    label='Role'
                    name='role'
                    labelId='role_select_label'
                    value={props.values.role || RoleEnum.NUMBER_0}
                    onChange={props.handleChange}
                    size='small'
                    disabled={props.values.role === RoleEnum.NUMBER_2 && !isViewerStaff}
                  >
                    <MenuItem value={RoleEnum.NUMBER_0}>Member</MenuItem>
                    <MenuItem value={RoleEnum.NUMBER_1}>Admin</MenuItem>
                    {props.values.role === RoleEnum.NUMBER_2 ? (
                      <MenuItem value={RoleEnum.NUMBER_2}>GrowthZone Staff</MenuItem>
                    ) : null}
                  </Select>
                </FormControl>
              </Grid>
              {isMentorshipEnabled && (
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id='mentorship_status_select_label'>Mentorship Status</InputLabel>
                    <Select
                      label='Mentorship Status'
                      aria-label='Mentorship Status'
                      name='mentorship_status'
                      labelId='mentorship_status_select_label'
                      value={props.values.mentorship_status}
                      onChange={props.handleChange}
                      disabled={props.values.role === RoleEnum.NUMBER_2 && !isViewerStaff}
                    >
                      <MenuItem value={MentorshipStatusEnum.NUMBER_2}>Not Applicable</MenuItem>
                      <MenuItem value={MentorshipStatusEnum.NUMBER_0}>Mentor</MenuItem>
                      <MenuItem value={MentorshipStatusEnum.NUMBER_1}>Mentee</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {/* // TODO tags and access passes */}
              {/* <Grid item xs={6} my={2}>
                <AutocompleteTags formik={props} label='Tag(s)' disabled={false} />
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={12}>
                <Select
                  sx={{ mb: 2 }}
                  name='access_passes'
                  value={props.values.access_passes}
                  onChange={props.handleChange}
                  fullWidth
                ></Select>
              </Grid> */}
            </Grid>
            <DrawerFooter
              onSubmit={props.submitForm}
              onCancel={() => handleClose()}
              submitting={props.isSubmitting}
              disabled={props.dirty && !props.isValid}
            />
          </>
        )}
      </Formik>
    </Box>
  );
};

export default EditUserForm;
